import { omit } from "lodash";
import React, { PureComponent } from "react";
import { PropTypes } from "prop-types";
import { Form, Icon, Input, Popup } from "semantic-ui-react";

import FormikAPIErrorMessage from "./FormikAPIErrorMessage.jsx";

import { getErrorStateFromProps } from "../formUtils.js";

class FormikSemanticNumberInput extends PureComponent {
  static propTypes = {
    field: PropTypes.object.isRequired,
    form: PropTypes.object.isRequired,
    min: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    step: PropTypes.number.isRequired,
    readOnly: PropTypes.bool.isRequired,
    required: PropTypes.bool.isRequired,
    label: PropTypes.string,
    help: PropTypes.string,
    placeholder: PropTypes.string,
    apiErrors: PropTypes.object
  };

  static defaultProps = {
    min: 0,
    max: 5,
    step: 1,
    readOnly: false,
    required: false
  };

  static CHANGE_DELAY = 2000;

  state = {
    value: 0
  };

  renderHelpPopup() {
    const { help } = this.props;

    if (!help) return null;

    return (
      <Popup
        trigger={
          <Icon name="help circle" color="orange" className="float-right" />
        }
        content={help}
        position="top right"
        offset={[11, 0]}
        wide
      />
    );
  }

  // -- HANDLERS -- //

  onChange = (evt, { value }) => {
    const {
      field: { name },
      form: { setFieldValue }
    } = this.props;

    const valueAsInt = parseInt(value, 10);

    // we set fields here to keep slider moving
    // doDelayedChange rounds the final value at rest for use
    setFieldValue("ignoreChangeInSideEffect", true);
    setFieldValue(name, value);

    this.setState({ value: valueAsInt });
  };

  onBlur = evt => {
    const {
      field: { name },
      form: { setFieldTouched }
    } = this.props;
    setFieldTouched(name, true);

    // apply change immediately rather than waiting for doDelayedChange
    this.doChange();
  };

  doDelayedChange = () => {
    clearTimeout(this.changeDelayTimer);
    this.changeDelayTimer = setTimeout(
      this.doChange,
      FormikSemanticSlider.CHANGE_DELAY
    );
  };

  doChange = evt => {
    const {
      field: { name },
      form: { setFieldValue }
    } = this.props;

    const boundedValue = this.boundValue(this.state.value);

    setFieldValue("ignoreChangeInSideEffect", false);
    setFieldValue(name, boundedValue);
  };

  // -- UTILS -- //

  boundValue = value => {
    const { min, max, step } = this.props;

    let boundedValue = !value || value < min ? min : value;
    boundedValue = boundedValue > max ? max : boundedValue;

    return Math.round(parseInt(boundedValue, 10) / step) * step;
  };

  render() {
    const {
      field,
      readOnly,
      disabled,
      label,
      required,
      min,
      max,
      step,
      apiErrors
    } = this.props;

    // set semantic-ui field error state
    const isError = getErrorStateFromProps(this.props);

    // needed to adjust field margin-bottom if error is shown
    const className = isError ? "errored" : null;

    const inputProps = {
      ...omit(this.props, "field", "form", "apiErrors", "label"),
      ...field,
      id: field.name,
      type: "number",
      error: isError,
      onChange: readOnly ? null : this.onChange,
      onBlur: readOnly ? null : this.onBlur
    };

    const isRequired = readOnly || disabled ? false : required;

    if (!readOnly) {
      return (
        <Form.Field error={isError} required={isRequired} className={className}>
          {this.renderHelpPopup()}
          {label && <label htmlFor={field.name}>{label || null}</label>}
          <Input
            {...inputProps}
            value={!Number.isNaN(field.value) ? field.value : ""}
            min={min}
            max={max}
            step={step}
          />
          <FormikAPIErrorMessage name={name} apiErrors={apiErrors} />
        </Form.Field>
      );
    }

    return (
      <Form.Field className={className}>
        {this.renderHelpPopup()}
        {label && <label>{label || null}</label>}
        <p>{value || "-"}</p>
      </Form.Field>
    );
  }
}

export default FormikSemanticNumberInput;
